import {searchAccountProductSubscriptions} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {getAvailableUserStatuses} from 'src/openapi/admin/apis/GetAvailableUserStatusesApi';
import {getUserDetails} from 'src/openapi/admin/apis/GetUserDetailsApi';
import {getUserSeatAssignments} from 'src/openapi/admin/apis/UserSeatAssignmentsApi';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {SeatAssignmentStatus} from 'src/openapi/admin/models/SeatAssignmentStatus';

import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';
import type {useAppDispatch} from 'src/store/hooks';

export type AccessPermissions = {platform: boolean, products: boolean, grant: boolean, revoke: boolean};
export type AccessDraft = Pick<UserDetails, 'typeCode' | 'statusCode'> & {seats: Record<string, boolean>};
export type AccessSnapshot = {
    details: UserDetails, terms: ProductSubscription[], assigned: Set<string>, statuses: string[],
};
type Dispatch = ReturnType<typeof useAppDispatch>;

export const readAccessDetails = async (dispatch: Dispatch, userId: number, signal?: AbortSignal) => {
    const response = await dispatch(getUserDetails({userId}, {signal}));
    if (!response.payload?.success) {
        throw response.payload?.data;
    }
    return response.payload.data;
};

const readTerms = async (dispatch: Dispatch, accountId: number, signal?: AbortSignal) => {
    const terms: ProductSubscription[] = [];
    for (let page = 1; ; page += 1) {
        const response = await dispatch(searchAccountProductSubscriptions({accountId,
            productSubscriptionSearchRequest: {pagination: {page, pageSize: 100},
                filters: {lifecycleStatuses: [ProductSubscriptionLifecycleStatus.ACTIVE,
                    ProductSubscriptionLifecycleStatus.PENDING]}},
        }, {signal}));
        if (!response.payload?.success) {
            throw response.payload?.data;
        }
        const {content = [], totalCount = 0} = response.payload.data;
        terms.push(...content);
        if (terms.length >= totalCount) {
            break;
        }
        if (!content.length || signal?.aborted) {
            throw new Error('Unable to load all Product Access Terms.');
        }
    }
    return terms.filter(term => ['ACTIVE', 'PENDING'].includes(term.lifecycleStatus))
        .sort((a, b) => a.endDate.localeCompare(b.endDate) || a.startDate.localeCompare(b.startDate) ||
            a.product.name.localeCompare(b.product.name) ||
            a.productSubscriptionId.localeCompare(b.productSubscriptionId));
};

const readAssignments = async (dispatch: Dispatch, userId: number, signal?: AbortSignal) => {
    const assigned = new Set<string>();
    let loaded = 0;
    for (let page = 1; ; page += 1) {
        const response = await dispatch(getUserSeatAssignments({userId, status: SeatAssignmentStatus.ACTIVE,
            page, size: 250}, {signal}));
        if (!response.payload?.success) {
            throw response.payload?.data;
        }
        const {content = [], totalCount = 0} = response.payload.data;
        content.forEach(item => assigned.add(item.productSubscriptionId));
        loaded += content.length;
        if (loaded >= totalCount) {
            return assigned;
        }
        if (!content.length || signal?.aborted) {
            throw new Error('Unable to load all seat assignments.');
        }
    }
};

export const loadAccessSnapshot = async (
    dispatch: Dispatch,
    {userId, accountId, permissions}: {userId: number, accountId: number, permissions: AccessPermissions},
    signal?: AbortSignal,
): Promise<AccessSnapshot> => {
    const readStatuses = async () => {
        if (!permissions.platform) {
            return [];
        }
        const response = await dispatch(getAvailableUserStatuses({accountId}, {signal}));
        if (!response.payload?.success) {
            throw response.payload?.data;
        }
        return response.payload.data;
    };
    const [details, terms, assigned, available] = await Promise.all([
        readAccessDetails(dispatch, userId, signal),
        permissions.products ? readTerms(dispatch, accountId, signal) : Promise.resolve([]),
        permissions.products ? readAssignments(dispatch, userId, signal) : Promise.resolve(new Set<string>()),
        readStatuses(),
    ]);
    const statuses = available.filter(status => details.statusCode === 'EMPTY' || status !== 'EMPTY');
    if (!statuses.includes(details.statusCode)) {
        statuses.unshift(details.statusCode);
    }
    return {details, terms, assigned, statuses};
};

export const draftFromSnapshot = ({details, terms, assigned}: AccessSnapshot): AccessDraft => ({
    typeCode: details.typeCode, statusCode: details.statusCode,
    seats: Object.fromEntries(terms.map(term => [
        term.productSubscriptionId, assigned.has(term.productSubscriptionId),
    ])),
});

export const changedSeats = (snapshot: AccessSnapshot, draft: AccessDraft) =>
    Object.entries(draft.seats).filter(([id, granted]) => granted !== snapshot.assigned.has(id));
export const platformChanged = (snapshot: AccessSnapshot, draft: AccessDraft) =>
    draft.typeCode !== snapshot.details.typeCode || draft.statusCode !== snapshot.details.statusCode;
